import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { selectFileRequirements, updateFileRequirements } from '../../../../../../features/gms-proposal-forms/attachment/file-requirement.slice';
import { useRequirementUpload } from '../../../../../../hooks/actions/use-requirement-upload';
import RequirementItem from './requirement-item';
import { selectedCurrentProposalStatusName } from '../../../../../../features/gms-state-and-modal/state-slice';
import { useIsSameUser } from '../../../../../../hooks/proposal/use-state-checker-proposal';
import { useRequiredSupportingDocumentsSocket } from '../../../../../../hooks/socket/use-required-supporting-documents';
import { FileRequirement } from '../../../../../../features/gms-proposal-forms/attachment/file-requirement.state';
import { Spinner } from '../../../../../../assets/icon/svg';
import LoadingViewFile from '../../../../../ui/loading-view-file';
import LoadingGlobal from '../../../../../ui/loading-global';

export default function ProposalRequirements() {

    const dispatch = useDispatch();
    const currentStatus = useSelector(selectedCurrentProposalStatusName);
    const statusKey = currentStatus?.toLowerCase() || "";
    const {
        handleShowModalConfirmBookedProposal,
        handleCloseModalConfirmBookedProposal,
        handleViewFile,
        // API
        fileRequirementErrorMessage,
        fileRequirement,
        isLoadingFileRequirement,
        isErrorFileRequirement,
        isLoadingViewFile,

        fileRequirementsStatus,
        fileRequirementStatusErrorMessage,
        isLoadingFileRequirementsStatus,
        isErrorFileRequirementsStatus,

        isLoadingUploadRequirementFile,

        isLoadingBookedProposal,
        // STATE
        fileInputRef,
        files,
        savedFiles,

        // FUNCTION
        handleBookedProposal,
        handleUploadClick,
        handleFileChange,
        handleSave,
        handleRemove
    } = useRequirementUpload();
    const reduxFiles = useSelector(selectFileRequirements);
    const isSameUser = useIsSameUser();
    const isLoadingFile = isLoadingViewFile
    const isLoadingGlobal = isLoadingUploadRequirementFile || isLoadingBookedProposal
    
    useEffect(()=>{
        if(fileRequirementsStatus){
            dispatch(updateFileRequirements(fileRequirementsStatus));
        }
    },[fileRequirementsStatus, dispatch]);

    useRequiredSupportingDocumentsSocket()
    const finalFileRequirement = fileRequirement ?? []

    const bookBtn = `flex items-center justify-center gap-1 w-full rounded px-3 py-1.5 text-xs transition disabled:cursor-not-allowed`;
    
    return (
        <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-md border border-gray-200 bg-white">
            {/* Header */}
            <div className="flex shrink-0 items-center justify-between gap-3 border-b bg-gray-50 px-3 py-2.5">
                <div className="flex min-w-0 items-center gap-2">
                    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-[#186cb2]/10">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="#186cb2" strokeWidth="1.8" className="h-4 w-4" >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                            <path strokeLinecap="round" strokeLinejoin="round" d="M14 2v6h6M8 13h8M8 17h6" />
                        </svg>
                    </div>
                    <div className="min-w-0">
                        <p className="text-xs font-semibold text-gray-800">
                            Required Attachments
                        </p>
                        <p className="truncate text-[11px] text-gray-500">
                            Upload all required supporting documents.
                        </p>
                    </div>
                </div>
                {finalFileRequirement?.length > 0 && (
                    <div className="shrink-0 rounded-full bg-gray-100 px-2 py-1 text-[10px] font-medium text-gray-500">
                        {Object.values(reduxFiles).filter(file => file.is_uploaded).length}/{finalFileRequirement.length}
                    </div>
                )}
            </div>

            {/* Content */}
            {isLoadingFileRequirement || isLoadingFileRequirementsStatus ? (
                <div className="flex min-h-[180px] flex-1 items-center justify-center">
                    <Spinner color="#186cb2" size={22} />
                </div>
            ) : isErrorFileRequirement || isErrorFileRequirementsStatus ? (
                <div className="flex min-h-[180px] flex-1 items-center justify-center px-4">
                    <div className="text-center">
                        <div className="mx-auto mb-2 flex h-9 w-9 items-center justify-center rounded-full bg-red-50">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-5 w-5 text-red-500" >
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.3 3.84 2.73 17a2 2 0 0 0 1.73 3h15.08a2 2 0 0 0 1.73-3L13.7 3.84a2 2 0 0 0-3.4 0z" />
                            </svg>
                        </div>
                        <p className="text-xs text-gray-600">
                            {fileRequirementErrorMessage || fileRequirementStatusErrorMessage || 'Unable to load requirements.'}
                        </p>
                    </div>
                </div>
            ) : (
                <div className="min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-y-contain p-2 [-webkit-overflow-scrolling:touch]">
                    <div className="space-y-1.5">
                        {fileRequirement?.map((fileR) => {
                            const currentFile = files[fileR.id];
                            const uploadedFile: FileRequirement = Object.values(reduxFiles).find((file) => file.id === fileR.id);
                            const isSaved = uploadedFile?.is_uploaded || savedFiles[fileR.id];
                            return (
                                <RequirementItem
                                    key={fileR.id}
                                    isSaved={isSaved}
                                    currentFile={currentFile}
                                    uploadedFile={uploadedFile}
                                    fileR={fileR}
                                    files={files}
                                    handleUploadClick={handleUploadClick}
                                    handleRemove={handleRemove}
                                    handleSave={handleSave}
                                    handleView={handleViewFile}
                                />
                            );
                        })}
                    </div>
                    <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileChange} />
                </div>
            )}
            {isLoadingFile && <LoadingViewFile />}
            {isLoadingGlobal && <LoadingGlobal />}
        </div>
    )
}